/**
 * Per-family roster row in the Add Families wizard. Fields mirror the
 * firstaction.html prototype's `afBlankRow()`, child name + birthdate,
 * parent contact info, enrollment window, schedule, and optional billing
 * fields when "Add for billing" is OFF (we collect rates per-row) or
 * deferred to Step 3 (we collect rates in bulk).
 */
export interface RosterRow {
  id: string;
  // Child
  childFirstName: string;
  childLastName: string;
  childBirthdate: string;
  // Optional. Brightwheel & Procare exports include allergies/medications; we
  // capture it so a provider's safety info isn't lost on import.
  allergies: string;
  // Optional. 'female' | 'male' | 'other' (GenderEnum). Exported by Brightwheel
  // & Procare; imported values are normalized to these.
  gender: string;
  // Parent / responsible billing contact
  parentFirstName: string;
  parentLastName: string;
  parentEmail: string;
  parentPhone: string;
  // Enrollment
  startDate: string;
  endDate: string;
  schedule: string;
  // Classroom. Required: a CCMS student must be attached to a room.
  room: string;
  // Billing (only used when addToBilling is OFF, per-row rates)
  billingFrequency: 'weekly' | 'biweekly' | 'monthly';
  // The RECURRING day invoices are due, which is how providers think about
  // billing ("we bill on the 1st", "we bill every Tuesday"): a day-of-month
  // ("1".."28") for monthly, or a weekday name ("tuesday") for weekly and
  // biweekly. `firstDueDateFor` turns it into the first actual due date.
  // Deliberately NOT a calendar date, and deliberately not `startDate` — that
  // one is the enrollment date, which billing must never overwrite.
  dueDate: string;
  // Whether each invoice bills the family for the upcoming period ('next', in
  // advance) or the period just finished ('prior', in arrears).
  billFor: 'prior' | 'next';
  // How this family pays: private pay (they pay full tuition) or subsidy (an
  // agency covers most, the family pays a copay). Drives what `billingAmount`
  // means and how the amount field is labeled.
  payType: 'private' | 'subsidy';
  // The amount we bill this family: monthly tuition (private) or monthly copay
  // (subsidy).
  billingAmount: string;
  // Legacy/optional: the agency-covered portion. Not shown in the current UI,
  // which captures a single amount per pay type.
  subsidy: string;
  // Optional note/memo added to the recurring invoice.
  billingNotes: string;
}

/**
 * Where the provider's roster is coming from. Picked on the new Step 0
 * source fork, drives which sub-flow the wizard runs.
 */
export type Source = 'switch' | 'spreadsheet' | 'manual' | 'invite';

export interface WizardState {
  step: 1 | 2 | 3 | 4;
  roster: RosterRow[];
  addToBilling: boolean;
  sendEmailsLater: boolean;
  emailSubject: string;
  emailBody: string;
  importedFromFileName?: string;
}

export interface RowValidation {
  isBlank: boolean;
  errors: string[];
}

export interface DoneSummary {
  added: number;
  contactsCreated: number;
  studentsCreated: number;
  invitesQueued: number;
  errors: string[];
}

/**
 * What we collect from the user in Step 3 when `addToBilling` is ON.
 * One bulk plan applied to every queued family, matches the prototype's
 * "we'll set rates and frequency for you, so the per-family billing
 * columns drop off the roster" copy on Step 2.
 */
export interface BulkBilling {
  amount: string;
  frequency: 'weekly' | 'biweekly' | 'monthly';
  startsOn: string;
}

/**
 * A room from the org's real rooms (useRooms). The wizard binds the Room
 * dropdown and CSV room-matching to these so a student is saved into a real
 * room — the students list is room-scoped and hides students whose room isn't
 * one of the director's rooms.
 */
export interface WizardRoom {
  id: string;
  name: string;
  isArchived?: boolean;
}

const WEEKDAY_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Turn a recurring due day into the first actual due date (YYYY-MM-DD): the
 * next occurrence strictly after `from`, so billing never starts in the past
 * and never fires the moment the roster is confirmed.
 *
 * Monthly takes a day-of-month ("1".."28" — days every month has, so the
 * schedule can't drift onto the 28th in February and stay there). Weekly and
 * biweekly take a weekday name; the plan then repeats every 1 or 2 weeks from
 * that date, which keeps the weekday.
 */
export function firstDueDateFor(
  dueDay: string,
  frequency: RosterRow['billingFrequency'],
  from: Date = new Date()
): string {
  const base = new Date(from.getFullYear(), from.getMonth(), from.getDate());

  if (frequency === 'monthly') {
    const day = Number(dueDay);
    if (!day || day < 1 || day > 28) return '';
    const candidate = new Date(base.getFullYear(), base.getMonth(), day);
    if (candidate > base) return iso(candidate);
    return iso(new Date(base.getFullYear(), base.getMonth() + 1, day));
  }

  const target = WEEKDAY_INDEX[dueDay?.toLowerCase()];
  if (target === undefined) return '';
  // 1..7 days ahead — never today, so the first invoice is always in the future.
  const delta = (target - base.getDay() + 7) % 7 || 7;
  const next = new Date(base);
  next.setDate(base.getDate() + delta);
  return iso(next);
}

export function blankRow(roomId = ''): RosterRow {
  return {
    id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    childFirstName: '',
    childLastName: '',
    childBirthdate: '',
    allergies: '',
    gender: '',
    parentFirstName: '',
    parentLastName: '',
    parentEmail: '',
    parentPhone: '',
    // New students are enrolled starting today by default (the add flow
    // doesn't ask for a start date; future-term enrollment is edited later).
    startDate: new Date().toISOString().slice(0, 10),
    endDate: '',
    schedule: '',
    room: roomId,
    billingFrequency: 'monthly',
    // Default monthly invoices to the 1st. Editable on the billing step.
    dueDate: '1',
    billFor: 'next',
    payType: 'private',
    billingAmount: '',
    subsidy: '',
    billingNotes: '',
  };
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Roster essentials: a row is valid to add once it has a child name, a valid
 * parent email, and a room (Brightwheel-minimal). Tuition is set on the
 * separate billing step, not here, so the roster stays fast to fill.
 */
export function validateRow(row: RosterRow): RowValidation {
  const isBlank =
    !row.childFirstName.trim() &&
    !row.childLastName.trim() &&
    !row.childBirthdate.trim() &&
    !row.parentFirstName.trim() &&
    !row.parentLastName.trim() &&
    !row.parentEmail.trim() &&
    !row.parentPhone.trim();

  if (isBlank) return { isBlank: true, errors: [] };

  // Required fields mirror the single-student Add Student form
  // (createStudentSchema in AddStudentPage): child first/last/birthday and a
  // parent first/last/email. Room is required because a CCMS student must be
  // attached to one (it's defaulted, so this rarely trips).
  const errors: string[] = [];
  if (!row.childFirstName.trim()) errors.push('child first name required');
  if (!row.childLastName.trim()) errors.push('child last name required');
  if (!row.childBirthdate.trim()) errors.push('child birthday required');
  if (!row.parentFirstName.trim()) errors.push('parent first name required');
  if (!row.parentLastName.trim()) errors.push('parent last name required');
  if (!row.parentEmail.trim()) {
    errors.push('parent email required');
  } else if (!EMAIL_RE.test(row.parentEmail.trim())) {
    errors.push('parent email looks invalid');
  }
  if (!row.room.trim()) errors.push('room required');

  return { isBlank: false, errors };
}

export interface FieldErrors {
  firstName?: string;
  lastName?: string;
  childBirthdate?: string;
  parentFirstName?: string;
  parentLastName?: string;
  email?: string;
  room?: string;
}

/**
 * Per-field validation for inline display on each input. Mirrors validateRow's
 * rules exactly so the field errors and the Continue gate never disagree.
 * Returns no errors for a blank (untouched) row so empty rows don't shout.
 */
export function fieldErrors(row: RosterRow): FieldErrors {
  if (validateRow(row).isBlank) return {};

  const errors: FieldErrors = {};
  if (!row.childFirstName.trim()) errors.firstName = 'Required';
  if (!row.childLastName.trim()) errors.lastName = 'Required';
  if (!row.childBirthdate.trim()) errors.childBirthdate = 'Required';
  if (!row.parentFirstName.trim()) errors.parentFirstName = 'Required';
  if (!row.parentLastName.trim()) errors.parentLastName = 'Required';
  if (!row.parentEmail.trim()) {
    errors.email = 'Required';
  } else if (!EMAIL_RE.test(row.parentEmail.trim())) {
    errors.email = 'Enter a valid email';
  }
  if (!row.room.trim()) errors.room = 'Select a room';
  return errors;
}

/**
 * True when a row still needs a valid monthly tuition. Used on the billing
 * step, which is where tuition is captured for recurring invoices.
 */
export function needsTuition(row: RosterRow): boolean {
  if (!row.billingAmount.trim()) return true;
  const amt = Number(row.billingAmount.replace(/[^0-9.]/g, ''));
  return !amt || amt <= 0;
}

/**
 * A row is billable once it has an amount. `billingAmount` holds the tuition
 * (private pay) or the copay (subsidy), per the row's pay type, so a single
 * check covers both.
 */
export function hasBillingAmount(row: RosterRow): boolean {
  const amount = Number((row.billingAmount || '').replace(/[^0-9.]/g, '')) || 0;
  return amount > 0;
}

/**
 * The rows that should get a recurring invoice plan, with `startDate` set to the
 * first actual due date.
 *
 * Shared by BOTH entry points into `confirmAddToBilling` — the Add Families
 * wizard and the standalone Add-to-billing page — so the two cannot drift.
 * `StepBilling` is shared between them and collects only `dueDate` (the
 * RECURRING day: day-of-month for monthly, weekday for weekly/biweekly). It
 * deliberately does NOT write `startDate`, because that field is the student's
 * enrollment date and billing must never overwrite it.
 *
 * `confirmAddToBilling` reads `startDate` and uses it as both `dateStart` and
 * `dateDue`, so the recurring day has to be turned into a concrete first due
 * date here. A page that skips this mapping bills every family on whatever
 * `startDate` already held — for rows built by `toRosterRow`, that is
 * `blankRow()`'s today default, i.e. immediately.
 *
 * @param rows   candidate rows.
 * @param hasStudent optional extra gate — the wizard bills only rows whose
 *   student document actually saved, so a failed row can never get a plan.
 *   Omitted on the Add-to-billing page, where every row is an existing student.
 *
 * Rows whose `dueDate` doesn't fit their `billingFrequency` (a day-of-month on a
 * weekly row, say) yield no due date and are dropped rather than billed on a
 * wrong day.
 */
export function toBillableRows(rows: RosterRow[], hasStudent?: (row: RosterRow) => boolean): RosterRow[] {
  return rows
    .filter((r) => hasBillingAmount(r) && (!hasStudent || hasStudent(r)))
    .map((r) => ({ ...r, startDate: firstDueDateFor(r.dueDate, r.billingFrequency) }))
    .filter((r) => r.startDate);
}

/** The child's full name as one string, however the provider entered it. */
export function childFullName(row: Pick<RosterRow, 'childFirstName' | 'childLastName'>): string {
  return (row.childFirstName.trim() + ' ' + row.childLastName.trim()).trim();
}

/** Case/space-insensitive key for matching a name against existing records. */
export function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Loose CSV parser, accepts comma or tab. The prototype maps columns
 * heuristically; we do the same, looking for header tokens that match
 * known fields (case-insensitive, partial-match).
 */
/**
 * Map one CSV header cell to a RosterRow field. Handles the real-world variety
 * from Brightwheel / Procare / Playground / Sheets / Excel:
 *  - email/phone are matched FIRST so "Guardian Email" / "Guardian Phone" aren't
 *    swallowed by the guardian → parent-last-name rule.
 *  - child vs. parent names are disambiguated by whether the header mentions a
 *    caregiver ("parent", "guardian", "primary guardian", "family", …).
 */
// A CSV header maps to a RosterRow field, or to one of two combined-name
// pseudo-fields we split into first/last on import.
type HeaderField = keyof RosterRow | 'childFullName' | 'parentFullName';

function classifyHeader(raw: string): HeaderField | null {
  const s = raw.toLowerCase().trim();
  if (!s) return null;
  if (/e-?mail/.test(s)) return 'parentEmail';
  if (/phone|mobile|\bcell\b|telephone/.test(s)) return 'parentPhone';
  if (/birth|\bdob\b/.test(s)) return 'childBirthdate';
  if (/allerg|medical|dietary|medication/.test(s)) return 'allergies';
  if (/gender|\bsex\b/.test(s)) return 'gender';
  if (/subsidy/.test(s)) return 'subsidy';
  if (/schedule|days/.test(s)) return 'schedule';
  if (/room|class/.test(s)) return 'room';
  if (/tuition|billing|\brate\b|amount/.test(s)) return 'billingAmount';
  if (/(start|enroll).*date|date.*(start|enroll)/.test(s)) return 'startDate';
  if (/end.*date|date.*end/.test(s)) return 'endDate';
  // Identifier / status / balance columns carry no roster value. Ignore them
  // explicitly so the name rules below don't misread "Student ID" or
  // "Enrollment Status" as a name.
  if (/\bid\b|identifier|\bstatus\b|balance|\bssn\b/.test(s)) return null;

  const isCaregiver = /parent|guardian|family|mother|father|caregiver/.test(s);
  const wantsFirst = /first|given/.test(s);
  const wantsLast = /last|surname/.test(s);
  if (isCaregiver) {
    if (wantsFirst) return 'parentFirstName';
    if (wantsLast) return 'parentLastName';
    // A caregiver column with no first/last qualifier ("Parent",
    // "Parent/Guardian", "Family") holds a full name; split it on import.
    return 'parentFullName';
  }
  if (wantsFirst) return 'childFirstName';
  if (wantsLast) return 'childLastName';
  // A bare name column ("Student Name", "Child Name", "Name") is the child's
  // full name.
  if (/name|student|child|\bkid\b/.test(s)) return 'childFullName';
  return null;
}

/**
 * Split a combined "Full Name" cell into first/last. Handles both
 * "First Last" and the common "Last, First" export convention.
 */
function splitFullName(raw: string): { first: string; last: string } {
  const t = raw.trim();
  if (!t) return { first: '', last: '' };
  if (t.includes(',')) {
    const [last, ...rest] = t.split(',');
    return { first: rest.join(',').trim(), last: last.trim() };
  }
  const parts = t.split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

/**
 * Split one CSV/TSV line into cells, honoring double-quoted fields so values
 * that contain commas ("Mujumdar, Adhrit", "$1,300", "Preschool (AM)") stay
 * intact instead of shattering across columns.
 */
function splitCsvRow(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',' || ch === '\t') {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

/** How many distinct known fields a row of cells maps to. Used to find the
 * real header row when a file carries title/blank preamble lines above it. */
function headerScore(cells: string[]): number {
  const keys = new Set<HeaderField>();
  cells.forEach((c) => {
    const key = classifyHeader(c);
    if (key) keys.add(key);
  });
  return keys.size;
}

/** Normalize common date exports (MM/DD/YYYY, M-D-YYYY) to the YYYY-MM-DD the
 * birthday field requires. Unrecognized formats pass through for manual fixup. */
function normalizeDate(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = t.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  return t;
}

/** Normalize an imported gender value ("Male", "F", "boy") to the GenderEnum
 * value the student record uses. Unknown-but-present values map to 'other'. */
function normalizeGender(raw: string): string {
  const t = raw.trim().toLowerCase();
  if (!t) return '';
  if (/^(f|female|girl|woman)/.test(t)) return 'female';
  if (/^(m|male|boy|man)/.test(t)) return 'male';
  return 'other';
}

/** Fuzzy-match an imported room name ("Toddler Room", "Toddlers") to one of the
 * org's real rooms; fall back to the default room id so the value is always a
 * room the students list can see. */
function matchRoom(raw: string, rooms: WizardRoom[], defaultRoomId: string): string {
  const t = raw.trim().toLowerCase();
  if (!t) return defaultRoomId;
  const hit = rooms.find((r) => {
    const l = (r.name || '').toLowerCase();
    return !!l && (l === t || t.includes(l.replace(/s$/, '')) || l.includes(t));
  });
  return hit ? hit.id : defaultRoomId;
}

// Footer/summary rows real exports tack on ("Total students: 18",
// "DELETED - moved to Maplewood", "Grand total…") — skipped so they don't
// become junk students.
const JUNK_ROW_RE = /^(total\b|grand total|subtotal|deleted\b|export\b|updated\b|note:)/i;

export function parseCsv(text: string, rooms: WizardRoom[] = [], defaultRoomId = ''): RosterRow[] {
  if (!text.trim()) return [];

  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return [];
  const rows = lines.map(splitCsvRow);

  // Find the header row rather than assuming line 0: exports often carry a
  // title and blank rows above the real header (e.g. a school name banner).
  // Pick the line that maps the most distinct known fields; require at least 2
  // so a stray data row can't masquerade as a header.
  let headerIdx = -1;
  let bestScore = 1;
  rows.forEach((cells, i) => {
    const score = headerScore(cells);
    if (score > bestScore) {
      bestScore = score;
      headerIdx = i;
    }
  });

  const colMap: Record<number, HeaderField> = {};
  let dataRows: string[][];
  if (headerIdx >= 0) {
    rows[headerIdx].forEach((h, i) => {
      const key = classifyHeader(h);
      if (key) colMap[i] = key;
    });
    dataRows = rows.slice(headerIdx + 1);
  } else {
    // No recognizable header → assume positional order: family,email,child,start.
    // This is a guess, and it is only trusted if it pans out, see the
    // credibility check below the mapping loop.
    colMap[0] = 'parentLastName';
    colMap[1] = 'parentEmail';
    colMap[2] = 'childFirstName';
    colMap[3] = 'startDate';
    dataRows = rows;
  }

  const parsed = dataRows
    .map((parts) => {
      if (parts.every((p) => !p)) return null;
      const firstCell = parts.find((p) => p) ?? '';
      if (JUNK_ROW_RE.test(firstCell)) return null;

      const row = blankRow(defaultRoomId);
      parts.forEach((val, i) => {
        const key = colMap[i];
        if (!key || !val) return;
        if (key === 'childBirthdate') row.childBirthdate = normalizeDate(val);
        else if (key === 'room') row.room = matchRoom(val, rooms, defaultRoomId);
        else if (key === 'gender') row.gender = normalizeGender(val);
        else if (key === 'childFullName') {
          const { first, last } = splitFullName(val);
          row.childFirstName = first;
          row.childLastName = last;
        } else if (key === 'parentFullName') {
          const { first, last } = splitFullName(val);
          row.parentFirstName = first;
          row.parentLastName = last;
        } else (row as any)[key] = val;
      });

      // Drop rows with no usable identity at all (a stray value in one column
      // isn't a student). Rows with a name or a parent email are kept for the
      // provider to review and complete.
      if (
        !row.childFirstName &&
        !row.childLastName &&
        !row.parentEmail &&
        !row.parentFirstName &&
        !row.parentLastName
      ) {
        return null;
      }
      return row;
    })
    .filter((r): r is RosterRow => r !== null);

  // When no header was recognized, everything above rested on a guess about
  // column order. Require the guess to produce at least one syntactically valid
  // email before believing it.
  //
  // Without this, any CSV at all parses "successfully": the positional fallback
  // always maps something, so a file with no student data in it (an invoice
  // export, a vendor report) came back as a roster of nonsense, with the header
  // row itself as the first student. The provider saw "✓ Imported from
  // <file>.csv" and had to hand-delete rows to work out what went wrong. The
  // `rows.length === 0` guard in Step2Roster that shows "Couldn't find any
  // students in this file" could never fire, because this never returned empty.
  //
  // A valid email is the right signal here: the positional format this fallback
  // exists for puts one in column 2, so a file without a single parseable
  // address was never that format. Files with a recognized header are
  // unaffected, they keep parsing on their headers, emails or not.
  if (headerIdx < 0 && !parsed.some((r) => EMAIL_RE.test(r.parentEmail.trim()))) return [];

  return parsed;
}

/** A recurring billing plan pulled from a tool's "billing plans" export
 * (Brightwheel's Bill Plans, Procare's current-tuition-plans report). */
export interface BillingImportRow {
  name: string; // student/child full name — the match key
  email: string; // payer email, if present (secondary match key)
  amount: string; // cleaned numeric string (the recurring charge)
  frequency: RosterRow['billingFrequency'] | '';
}

/** Normalize a frequency string ("Monthly", "every other week") to our enum. */
export function normalizeFrequency(raw: string): RosterRow['billingFrequency'] | '' {
  const t = raw.trim().toLowerCase();
  if (/bi-?week|every other week|semi-?month|fortnight/.test(t)) return 'biweekly';
  if (/week/.test(t)) return 'weekly';
  if (/month/.test(t)) return 'monthly';
  return '';
}

function classifyBillingHeader(raw: string): keyof BillingImportRow | null {
  const s = raw.toLowerCase().trim();
  if (!s) return null;
  if (/e-?mail/.test(s)) return 'email';
  if (/frequency|cycle|\binterval\b|billing schedule/.test(s)) return 'frequency';
  // Amount: matches Original/Discounted/Final Amount, Tuition, Rate, etc. When a
  // file has several (Brightwheel), the last column wins → the Final amount.
  if (/final|amount|tuition|\brate\b|charge|price|\btotal\b/.test(s)) return 'amount';
  if (/student|child|name/.test(s)) return 'name';
  return null;
}

/**
 * Parse a billing-plan export into recurring-plan rows. Heuristic column
 * detection, like parseCsv, tolerant of Brightwheel/Procare header variety.
 * The caller matches these to the roster (by student name / payer email).
 */
export function parseBillingCsv(text: string): BillingImportRow[] {
  if (!text.trim()) return [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return [];

  const hasHeader = /[a-zA-Z]/.test(lines[0]);
  const rawHeader = hasHeader ? lines[0].split(/[,\t]/) : [];
  const dataLines = hasHeader ? lines.slice(1) : lines;

  const colMap: Record<number, keyof BillingImportRow> = {};
  rawHeader.forEach((h, i) => {
    const key = classifyBillingHeader(h);
    if (key) colMap[i] = key;
  });

  return dataLines
    .map((line) => {
      const parts = line.split(/[,\t]/).map((s) => s.trim());
      if (parts.every((v) => !v)) return null;
      const row: BillingImportRow = { name: '', email: '', amount: '', frequency: '' };
      parts.forEach((val, i) => {
        const key = colMap[i];
        if (!key || !val) return;
        if (key === 'amount') row.amount = val.replace(/[^0-9.]/g, '');
        else if (key === 'frequency') row.frequency = normalizeFrequency(val);
        else row[key] = val;
      });
      return row.amount || row.name || row.email ? row : null;
    })
    .filter((r): r is BillingImportRow => r !== null);
}

/**
 * Birthdate → age in whole years (display string). Returns empty string
 * if the date is invalid or in the future.
 */
export function ageYears(iso: string): string {
  if (!iso) return '';
  const b = new Date(iso);
  if (Number.isNaN(b.getTime())) return '';
  const now = new Date();
  let years = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) years--;
  return years >= 0 && years < 25 ? `${years} yr` : '';
}
